import { QUESTION_TYPES } from "./surveyLiterals";

const withoutId = ({ id, ...rest }) => rest;

const isCopyOf = (copy, original) =>
  copy.code !== original.code && copy.code.startsWith(`${original.code}_`) && /^\d+$/.test(copy.code.slice(original.code.length + 1));

const cloneQuestion = (question, code) => ({
  ...withoutId(question),
  code,
  translations: (question.translations || []).map(withoutId),
  links: (question.links || []).map(withoutId),
  selections: (question.selections || []).map((sel) => ({ ...withoutId(sel), translations: (sel.translations || []).map(withoutId) })),
});

// Original matrix child plus its generated copies: same Group ID (questionsGroup) and code family (CODE, CODE_2, ...).
export const getMatrixGroupMembers = (questions, code) => {
  const edited = questions.find((que) => que.code === code);
  if (!edited || !edited.questionsGroup) return edited ? [edited] : [];
  const sameGroup = questions.filter((que) => que.questionsGroup === edited.questionsGroup);
  const original = sameGroup.find((que) => isCopyOf(edited, que)) || edited;
  return sameGroup.filter((que) => que === original || isCopyOf(que, original));
};

export const isGeneratedMatrixCopy = (questions, question) =>
  question.questionsGroup > 0 &&
  questions.some((orig) => orig.questionsGroup === question.questionsGroup && isCopyOf(question, orig));

// Rows are Labels depending on the matrix; columns are the other (original) children, with one cell question per row.
export const buildMatrixPreview = (matrix, questions) => {
  const byShowOrder = (a, b) => a.showOrder - b.showOrder;
  const children = questions.filter(
    (que) =>
      (que.codeQuestionDepends && que.codeQuestionDepends === matrix.code) ||
      (matrix.id != null && que.idQuestionDepends != null && String(que.idQuestionDepends) === String(matrix.id))
  );
  const rows = children.filter((que) => que.type === QUESTION_TYPES.LABEL).sort(byShowOrder);
  const columns = children
    .filter((que) => que.type !== QUESTION_TYPES.LABEL && que.type !== QUESTION_TYPES.MATRIX && !isGeneratedMatrixCopy(questions, que))
    .sort(byShowOrder)
    .map((original) => ({
      question: original,
      cells: rows.map((_, i) => (i === 0 ? original : children.find((que) => que.code === `${original.code}_${i + 1}`) || original)),
    }));
  return { rows, columns, memberCodes: new Set(children.map((que) => que.code)) };
};

// Non-Label questions depending on a Matrix are duplicated once per Label depending on the same Matrix.
// The original and its copies share a Group ID (questionsGroup); the original is the source of truth.
export const expandMatrixDependentQuestions = (sections) => {
  const allQuestions = sections.flatMap((sec) => sec.questions || []);
  const matrixCodes = new Set(allQuestions.filter((que) => que.type === QUESTION_TYPES.MATRIX).map((que) => que.code));

  const labelCountByMatrix = {};
  allQuestions
    .filter((que) => que.type === QUESTION_TYPES.LABEL && matrixCodes.has(que.codeQuestionDepends))
    .forEach((que) => {
      labelCountByMatrix[que.codeQuestionDepends] = (labelCountByMatrix[que.codeQuestionDepends] || 0) + 1;
    });

  const isMatrixChild = (que) =>
    matrixCodes.has(que.codeQuestionDepends) && que.type !== QUESTION_TYPES.LABEL && que.type !== QUESTION_TYPES.MATRIX;

  return sections.map((sec) => {
    const questions = sec.questions || [];
    const isGeneratedCopy = (que) =>
      questions.some((orig) => isCopyOf(que, orig) && (isMatrixChild(orig) || (orig.questionsGroup > 0 && orig.questionsGroup === que.questionsGroup)));
    if (!questions.some((que) => isMatrixChild(que) || isGeneratedCopy(que))) return sec;

    const remaining = questions.filter((que) => !isGeneratedCopy(que)).sort((a, b) => a.showOrder - b.showOrder);
    let nextGroup = Math.max(0, ...remaining.map((que) => que.questionsGroup || 0)) + 1;

    const expanded = remaining
      .flatMap((que) => {
        const labelCount = isMatrixChild(que) ? labelCountByMatrix[que.codeQuestionDepends] || 0 : 0;
        if (labelCount < 2) return [que];
        const source = que.questionsGroup > 0 ? que : { ...que, questionsGroup: nextGroup++ };
        const copies = Array.from({ length: labelCount - 1 }, (_, i) => cloneQuestion(source, `${que.code}_${i + 2}`));
        return [source, ...copies];
      })
      .map((que, index) => ({ ...que, showOrder: index + 1 }));

    return { ...sec, questions: expanded };
  });
};
